// wyvio — Logotype officiel (SVG inline, aucun asset externe).
//
// Design :
//   • Icône — V lime + petit v blanc imbriqué + point lime (forme un "W" stylisé)
//   • Wordmark — "wyvio" lowercase, Sora 600, terminaisons arrondies
//   • Point lime dans le "o" pour rappeler l'accent
//
// Palette et brand centralisés dans `branding/config`.
// Pour remplacer par un SVG officiel externe : réécrire uniquement ce fichier.

import { palette } from "@/branding/config";

/**
 * Glyphe wyvio — deux V imbriqués (grand lime + petit blanc) + point d'accent.
 * viewBox 240x200. Peut être colorisé pour variantes (mono blanc/noir).
 */
const WyvioGlyph = ({
  size = 28,
  primary = palette.primary,
  secondary = palette.paper,
  testId,
}) => {
  // Le glyphe est légèrement plus large que haut (ratio 240/200 = 1.2)
  const height = size;
  const width = Math.round(size * 1.2);
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 240 200"
      width={width}
      height={height}
      role="img"
      aria-label="wyvio"
      data-testid={testId}
    >
      {/* Grand V (lime) */}
      <path
        d="M 34 34 L 100 170 L 166 34"
        fill="none"
        stroke={primary}
        strokeWidth={34}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      {/* Petit v (blanc) */}
      <path
        d="M 128 78 L 168 158 L 208 78"
        fill="none"
        stroke={secondary}
        strokeWidth={20}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      {/* Point d'accent (lime) */}
      <circle cx="168" cy="184" r="8" fill={primary} />
    </svg>
  );
};

export const LogoGlyph = ({
  size = 28,
  color = palette.primary,
  secondary = palette.paper,
  className = "",
  testId = "brand-glyph",
}) => (
  <span className={className} style={{ display: "inline-flex" }}>
    <WyvioGlyph size={size} primary={color} secondary={secondary} testId={testId} />
  </span>
);

/**
 * Wordmark "wyvio" — Sora 600, lowercase.
 * Un point lime remplace visuellement le centre du "o".
 * Sora est chargée via Google Fonts dans index.html.
 */
export const LogoWordmark = ({
  size = 24,
  color = palette.paper,
  dotColor = palette.primary,
  className = "",
  testId = "brand-wordmark",
}) => (
  <span
    data-testid={testId}
    className={`inline-flex items-baseline ${className}`}
    style={{
      fontFamily: `"Sora", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif`,
      fontWeight: 600,
      fontSize: size,
      lineHeight: 1,
      letterSpacing: "-0.02em",
      color,
      whiteSpace: "nowrap",
    }}
  >
    wyv
    {/* le "i" et le "o" sont accolés — on positionne un point vert au centre du "o" via un span relatif */}
    <span style={{ position: "relative", display: "inline-block" }}>
      io
      <span
        aria-hidden="true"
        style={{
          position: "absolute",
          // centre approximatif du "o" (2ème caractère de "io")
          left: "72%",
          top: "50%",
          width: Math.max(2, Math.round(size * 0.11)),
          height: Math.max(2, Math.round(size * 0.11)),
          borderRadius: "50%",
          background: dotColor,
          transform: "translate(-50%, -50%)",
        }}
      />
    </span>
  </span>
);

const containerBg = {
  dark:  palette.ink,
  paper: palette.paper,
  none:  "transparent",
};

/**
 * Logo composé — glyphe wyvio + wordmark.
 *
 * Props :
 *  - `variant`    : "full" | "compact" | "wordmark"     (default full)
 *  - `size`       : "xs" | "sm" | "md" | "lg" | "xl"    (default md)
 *  - `background` : "dark" | "paper" | "none"           (default dark)
 *  - `rounded`    : classe tailwind pour l'arrondi du container
 */
const Logo = ({
  variant = "full",
  size = "md",
  background = "dark",
  rounded = "rounded-xl",
  className = "",
  testId = "brand-logo",
}) => {
  const dims = {
    xs: { pad: "px-2.5 py-1.5", glyph: 20, word: 16, gap: 8 },
    sm: { pad: "px-3 py-2",     glyph: 26, word: 20, gap: 10 },
    md: { pad: "px-3.5 py-2.5", glyph: 30, word: 24, gap: 12 },
    lg: { pad: "px-4 py-3",     glyph: 38, word: 32, gap: 14 },
    xl: { pad: "px-6 py-4",     glyph: 54, word: 46, gap: 18 },
  }[size] || {};

  const onDark = background === "dark";
  const wordColor = onDark ? palette.paper : palette.ink;
  const glyphSecondary = onDark ? palette.paper : palette.ink;

  const containerStyle = {
    background: containerBg[background] || containerBg.none,
  };

  if (variant === "compact") {
    return (
      <span
        data-testid={testId}
        className={`inline-flex items-center justify-center ${rounded} ${dims.pad} ${className}`}
        style={containerStyle}
        aria-label="wyvio"
      >
        <WyvioGlyph size={dims.glyph} primary={palette.primary} secondary={glyphSecondary} />
      </span>
    );
  }

  if (variant === "wordmark") {
    return (
      <span
        data-testid={testId}
        className={`inline-flex items-center ${rounded} ${dims.pad} ${className}`}
        style={containerStyle}
      >
        <LogoWordmark size={dims.word} color={wordColor} dotColor={palette.primary} />
      </span>
    );
  }

  // full — glyphe + wordmark
  return (
    <span
      data-testid={testId}
      className={`inline-flex items-center ${rounded} ${dims.pad} ${className}`}
      style={{ ...containerStyle, gap: dims.gap }}
    >
      <WyvioGlyph size={dims.glyph} primary={palette.primary} secondary={glyphSecondary} />
      <LogoWordmark size={dims.word} color={wordColor} dotColor={palette.primary} />
    </span>
  );
};

export default Logo;
